import { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { MapPin, Package, Users, Truck, Loader2, Clock, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import OrderMap from "../components/OrderMap";

export default function AdminLiveOps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOps = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/live-operations");
      setData(data);
    } catch { /* empty */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchOps();
    const interval = setInterval(fetchOps, 10000);
    return () => clearInterval(interval);
  }, [fetchOps]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;

  const allOrders = [...(data?.active_orders || []), ...(data?.pending_orders || [])];

  return (
    <div className="space-y-6" data-testid="admin-live-ops-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Live Operations</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time view of all active deliveries and agents</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full bg-green-500 live-dot" />
            <span className="text-slate-500">Auto-refreshing every 10s</span>
          </div>
          <Button onClick={fetchOps} variant="outline" className="rounded-xl border-slate-200" data-testid="refresh-ops-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-center" data-testid="ops-active-count">
          <Truck className="h-6 w-6 text-blue-600 mx-auto mb-1" />
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{data?.total_active ?? 0}</p>
          <p className="text-xs text-slate-500">Active Deliveries</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center" data-testid="ops-agents-count">
          <Users className="h-6 w-6 text-green-600 mx-auto mb-1" />
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{data?.total_agents_online ?? 0}</p>
          <p className="text-xs text-slate-500">Agents Online</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center" data-testid="ops-pending-count">
          <Clock className="h-6 w-6 text-amber-600 mx-auto mb-1" />
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{data?.total_pending ?? 0}</p>
          <p className="text-xs text-slate-500">Pending Pickup</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="ops-map-container">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#FF6B00]" /> Operations Map
            </h2>
          </div>
          <div className="h-[500px]">
            {selectedOrder ? (
              <OrderMap
                pickupLocation={selectedOrder.pickup_location}
                deliveryLocation={selectedOrder.delivery_location}
                agentLocation={null}
                status={selectedOrder.status}
              />
            ) : allOrders.length > 0 && allOrders[0].pickup_location ? (
              <OrderMap
                pickupLocation={allOrders[0].pickup_location}
                deliveryLocation={allOrders[0].delivery_location}
                agentLocation={null}
                status={allOrders[0].status}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-slate-50">
                <div className="text-center">
                  <MapPin className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No active orders to display on map</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order List */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="ops-order-list">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 flex items-center gap-2">
              <Package className="h-4 w-4 text-[#FF6B00]" /> Active Orders ({allOrders.length})
            </h2>
          </div>
          <div className="overflow-y-auto max-h-[500px] divide-y divide-slate-50">
            {allOrders.length === 0 ? (
              <div className="p-8 text-center"><p className="text-sm text-slate-400">No active orders</p></div>
            ) : allOrders.map((order) => (
              <div
                key={order.tracking_id}
                className={`p-3 cursor-pointer transition-colors hover:bg-slate-50 ${selectedOrder?.tracking_id === order.tracking_id ? "bg-orange-50/50 border-l-2 border-[#FF6B00]" : ""}`}
                onClick={() => setSelectedOrder(order)}
                data-testid={`ops-order-${order.tracking_id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-xs font-semibold text-slate-700">{order.tracking_id}</span>
                  <StatusBadge status={order.status} size="sm" />
                </div>
                <p className="text-xs text-slate-600 truncate">{order.package_description}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-slate-400 truncate max-w-[120px]">{order.pickup_address}</span>
                  {order.agent_name && <span className="text-[10px] text-blue-600 font-medium">{order.agent_name}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Online Agents */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="ops-online-agents">
        <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-4">Online Agents</h2>
        {(data?.online_agents || []).length === 0 ? (
          <p className="text-sm text-slate-400">No agents currently online</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(data?.online_agents || []).map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl">
                <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center">
                  <span className="text-green-800 text-sm font-semibold">{(a.name || "A")[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{a.name}</p>
                  <p className="text-[10px] text-green-600 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Online</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
